import { CurrencyFormatter } from '@shared/utils/currency';
import type { PriceConfig } from '../routes/CardCustomizer/CardCustomizer.types';
import { CardDieCut, CardDelivery, CardEnvelopeReturnAddressLayout, CardEnvelopeRecipientAddressLayout, CardPaperType } from '../routes/CardCustomizer/CardCustomizer.types';
import type { CurrencyRateData } from '@apps/card/hooks/useCurrencyConversionRate';

const formatter = new CurrencyFormatter({ locale: 'en', defaultCurrency: 'USD' });

type QuantityPriceConfig = {
  quantity: number;
  totalPriceInMinorUnits: number;
  individualPriceString: string;
  individualPriceInMinorUnits: number;
  totalPriceString: string;
};

// [4, 10, 20, 30, 40, ..up to 400 (inclusive)]
export const PRINT_SAMPLE_ORDER_QUANTITY = 4;
const AVAILABLE_QUANTITIES = [PRINT_SAMPLE_ORDER_QUANTITY, ...new Array(40).fill(null).map((_, index) => (index + 1) * 10)];

const _generatePriceConfig = ({ getIndividualPriceByQuantityInMinorUnits }: { getIndividualPriceByQuantityInMinorUnits: (quantity: number) => number }) => {
  const configByQuantity: Record<number, QuantityPriceConfig> = {};

  AVAILABLE_QUANTITIES.forEach(quantity => {
    const individPriceInMinorUnits = getIndividualPriceByQuantityInMinorUnits(quantity);
    const totalPriceInMinorUnits = quantity * individPriceInMinorUnits;

    const config: QuantityPriceConfig = {
      quantity,
      totalPriceInMinorUnits,
      individualPriceString: formatter.formatCurrency(individPriceInMinorUnits / 100, { form: 'auto' }),
      individualPriceInMinorUnits: individPriceInMinorUnits,
      totalPriceString: formatter.formatCurrency(totalPriceInMinorUnits / 100, { form: 'auto' }),
    };

    configByQuantity[quantity] = config;
  });

  return configByQuantity;
};

export const PRICE_CONFIG: PriceConfig = {
  quantity: {
    thankYou: _generatePriceConfig({
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        if (quantity >= 100) {
          return 139;
        } else if (quantity >= 70) {
          return 149;
        } else if (quantity >= 40) {
          return 169;
        }

        return 239;
      },
    }),
    saveTheDate: _generatePriceConfig({
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        if (quantity >= 100) {
          return 139;
        } else if (quantity >= 70) {
          return 149;
        } else if (quantity >= 40) {
          return 169;
        }

        return 239;
      },
    }),
    invitation: _generatePriceConfig({
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        if (quantity >= 100) {
          return 179;
        } else if (quantity >= 70) {
          return 189;
        } else if (quantity >= 40) {
          return 229;
        }

        return 319;
      },
    }),
    holiday: _generatePriceConfig({
      // TEMPORARILY COPIED FROM SAVETHEDATE
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        if (quantity >= 100) {
          return 139;
        } else if (quantity >= 70) {
          return 149;
        } else if (quantity >= 40) {
          return 169;
        }

        return 239;
      },
    }),
    enclosure: _generatePriceConfig({
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        if (quantity >= 100) {
          return 119;
        } else if (quantity >= 70) {
          return 129;
        } else if (quantity >= 40) {
          return 139;
        }

        return 219;
      },
    }),
    menu: _generatePriceConfig({
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        if (quantity >= 100) {
          return 139;
        } else if (quantity >= 70) {
          return 149;
        } else if (quantity >= 40) {
          return 169;
        }

        return 239;
      },
    }),
    // Temporary config until BE pricing changes are avaialble
    napkin: _generatePriceConfig({
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        if (quantity >= 100) {
          return 139;
        } else if (quantity >= 70) {
          return 149;
        } else if (quantity >= 40) {
          return 169;
        }

        return 239;
      },
    }),
    monogram: _generatePriceConfig({
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        if (quantity >= 100) {
          return 139;
        } else if (quantity >= 70) {
          return 149;
        } else if (quantity >= 40) {
          return 169;
        }

        return 239;
      },
    }),
    // Temporary config until BE pricing changes are avaialble
    sign: _generatePriceConfig({
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        if (quantity >= 100) {
          return 139;
        } else if (quantity >= 70) {
          return 149;
        } else if (quantity >= 40) {
          return 169;
        }

        return 239;
      },
    }),
  },
  paperType: {
    [CardPaperType.signature]: _generatePriceConfig({
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        return 0;
      },
    }),
    [CardPaperType.smooth]: _generatePriceConfig({
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        return 0;
      },
    }),
    [CardPaperType.semiGloss]: _generatePriceConfig({
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        return 0;
      },
    }),
    [CardPaperType.pearlescent]: _generatePriceConfig({
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        if (quantity <= 10) {
          return 70;
        }

        if (quantity <= 70) {
          return 35;
        }

        return 30;
      },
    }),
    [CardPaperType.doubleThick]: _generatePriceConfig({
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        if (quantity <= 10) {
          return 190;
        }

        if (quantity <= 70) {
          return 110;
        }

        return 80;
      },
    }),
    [CardPaperType.tripleThick]: _generatePriceConfig({
      getIndividualPriceByQuantityInMinorUnits: quantity => {
        if (quantity <= 10) {
          return 220;
        }

        if (quantity <= 70) {
          return 180;
        }

        return 140;
      },
    }),
  },
  shape: {
    [CardDieCut.rectangle]: {
      code: undefined,
      label: 'Rectangle',
      individualPriceString: undefined,
      getTotalPriceStringByQuantity: () => 'FREE',
      getTotalPriceByQuantityInMinorUnits: quantity => {
        return 0;
      },
    },
    [CardDieCut.rounded]: {
      code: 'round',
      label: 'Rounded',
      individualPriceString: formatter.formatCurrency(0.25, { form: 'short' }),
      getTotalPriceStringByQuantity: quantity => formatter.formatCurrency(0.25 * quantity, { form: 'short' }),
      getTotalPriceByQuantityInMinorUnits: quantity => {
        return quantity * 25;
      },
    },
    [CardDieCut.arch]: {
      code: 'arch5x7',
      label: 'Arch',
      individualPriceString: formatter.formatCurrency(0.25, { form: 'short' }),
      getTotalPriceStringByQuantity: quantity => formatter.formatCurrency(0.25 * quantity, { form: 'short' }),
      getTotalPriceByQuantityInMinorUnits: quantity => {
        return quantity * 25;
      },
    },
  },
  delivery: {
    [CardDelivery.economy]: 999, // $9.99,
    [CardDelivery.standard]: 1495, // $14.95,
    [CardDelivery.express]: 3995, // $39.95
    [CardDelivery.upsGroundCanadaStandard]: 3500, // $35.00
    [CardDelivery.upsCanadaExpressSaver]: 5500, // $55.00
    [CardDelivery.upsInternationalExpressSaver]: 5500, // $55.00
  },
  returnAddress: {
    [CardEnvelopeReturnAddressLayout.back]: {
      individualPriceString: undefined,
      getTotalPriceByQuantityInMinorUnits: () => 0,
      getTotalPriceStringByQuantity: () => 'FREE',
    },
    [CardEnvelopeReturnAddressLayout.front]: {
      individualPriceString: undefined,
      getTotalPriceByQuantityInMinorUnits: () => 0,
      getTotalPriceStringByQuantity: () => 'FREE',
    }, // $0.35
    [CardEnvelopeReturnAddressLayout.none]: {
      individualPriceString: undefined,
      getTotalPriceByQuantityInMinorUnits: () => 0,
      getTotalPriceStringByQuantity: () => 'FREE',
    },
  },
  recipientAddress: {
    [CardEnvelopeRecipientAddressLayout.front]: {
      individualPriceString: undefined,
      getTotalPriceByQuantityInMinorUnits: () => 0,
      getTotalPriceStringByQuantity: () => 'FREE',
    },
    [CardEnvelopeRecipientAddressLayout.none]: {
      individualPriceString: undefined,
      getTotalPriceByQuantityInMinorUnits: () => 0,
      getTotalPriceStringByQuantity: () => 'FREE',
    },
  },
};

export interface DigitalPriceConfig {
  delivery: {
    [key: string]: {
      quantity: number;
      price: number;
      premiumUpcharge: number;
      diamondUpcharge: number;
      default?: boolean;
    };
  };
}

export const DEFAULT_DIGITAL_PRICE_CONFIG: DigitalPriceConfig = {
  delivery: {
    '200': {
      quantity: 200,
      price: 4900,
      premiumUpcharge: 4900,
      diamondUpcharge: 9900,
    },
    '500': {
      quantity: 500,
      price: 7900,
      premiumUpcharge: 6900,
      diamondUpcharge: 11900,
    },
    '1000': {
      quantity: 1000,
      price: 9900,
      premiumUpcharge: 9900,
      diamondUpcharge: 14900,
    },
  },
};

/**
 * Default currency conversion rate for orders in the base currency (USD).
 */
export const DEFAULT_CURRENCY_CONVERSION_RATE: CurrencyRateData = {
  exchangeRate: 1,
  taxesAndFeesMultiplier: 1,
  fixedCostPerCardInMinorUnits: 0,
};
